import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ChatRoom, SendMessageRequest } from 'amazon-ivs-chat-messaging';
import communityApi from '../../services/communityApi';
import { readStoredUser } from '../../services/authApi';
import './StreamChat.css';

export default function StreamChat({ streamerUsername }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [connectionState, setConnectionState] = useState('disconnected');
  const roomRef = useRef(null);
  const bottomRef = useRef(null);
  const currentUser = readStoredUser();
  const displayNameRef = useRef(currentUser?.username || currentUser?.id || 'viewer');
  const profileImageRef = useRef(currentUser?.profileImage || currentUser?.avatar || '');
  const roomId = streamerUsername ? `user:${streamerUsername}` : '';

  const tokenProvider = useCallback(async () => {
    const data = await communityApi.issueChatToken(roomId);
    if (data.displayName) displayNameRef.current = data.displayName;
    if (data.profileImage) profileImageRef.current = data.profileImage;
    return {
      token: data.token,
      sessionExpirationTime: data.sessionExpirationTime
        ? new Date(data.sessionExpirationTime)
        : undefined,
      tokenExpirationTime: data.tokenExpirationTime
        ? new Date(data.tokenExpirationTime)
        : undefined,
    };
  }, [roomId]);

  useEffect(() => {
    if (!roomId) return undefined;

    let cancelled = false;

    const room = new ChatRoom({
      regionOrUrl: 'us-east-1',
      tokenProvider,
    });
    roomRef.current = room;

    const unsubConnecting = room.addListener('connecting', () => setConnectionState('connecting'));
    const unsubConnect = room.addListener('connect', () => {
      setConnectionState('connected');
      setError('');
      communityApi
        .fetchHistory(roomId, { limit: 30 })
        .then((resp) => {
          if (cancelled) return;
          const historical = (resp?.messages || []).map((h) => ({
            id: h.id,
            content: h.content,
            sender: { userId: h.senderUserId || undefined },
            attributes: {
              displayName: h.senderDisplayName || undefined,
              profileImage: h.senderProfileImage || undefined,
            },
          }));
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            return [...historical.filter((h) => !seen.has(h.id)), ...prev];
          });
        })
        .catch(() => {});
    });
    const unsubDisconnect = room.addListener('disconnect', (reason) => {
      setConnectionState('disconnected');
      if (reason !== 'clientDisconnect') {
        setError('Connection lost. Reconnecting…');
      }
    });
    const unsubMessage = room.addListener('message', (msg) => {
      setMessages((prev) => [
        ...prev,
        msg,
      ]);
    });
    const unsubDelete = room.addListener('messageDelete', (event) => {
      setMessages((prev) => prev.filter((m) => m.id !== event.messageId));
    });

    room.connect();

    return () => {
      cancelled = true;
      unsubConnecting();
      unsubConnect();
      unsubDisconnect();
      unsubMessage();
      unsubDelete();
      room.disconnect();
      roomRef.current = null;
      setConnectionState('disconnected');
      setMessages([]);
    };
  }, [roomId, tokenProvider]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending || !roomRef.current) return;
    setSending(true);
    setError('');
    try {
      const attrs = {
        displayName: displayNameRef.current || currentUser?.username || currentUser?.id || 'viewer',
      };
      if (profileImageRef.current) attrs.profileImage = profileImageRef.current;
      const req = new SendMessageRequest(text, attrs);
      await roomRef.current.sendMessage(req);
      setInput('');
    } catch (err) {
      setError(err.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const statusLabel = connectionState === 'connected'
    ? 'Connected'
    : connectionState === 'connecting' ? 'Connecting…' : 'Disconnected';

  return (
    <div className="stream-chat">
      <div className="stream-chat__header">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Community Chat
        <span className={`stream-chat__status ${connectionState === 'connected' ? 'stream-chat__status--on' : 'stream-chat__status--off'}`}>
          {statusLabel}
        </span>
      </div>

      <div className="stream-chat__messages">
        {messages.length === 0 ? (
          <p className="stream-chat__empty">No messages yet. Chat will appear here once connected.</p>
        ) : (
          messages.map((msg) => {
            const isMine = msg.sender?.userId === currentUser?.id;
            const displayName = msg.attributes?.displayName
              || (isMine ? currentUser?.username : msg.sender?.userId)
              || 'unknown';
            const profileImage = msg.attributes?.profileImage
              || (isMine ? currentUser?.avatar || currentUser?.profileImage : '')
              || '';

            return (
              <div
                key={msg.id}
                className={`stream-chat__msg${isMine ? ' stream-chat__msg--mine' : ''}`}
              >
                <div className="stream-chat__msg-avatar" aria-hidden="true">
                  {profileImage ? (
                    <img src={profileImage} alt="" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="stream-chat__msg-avatar-fallback">
                      {displayName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="stream-chat__msg-body">
                  <span className="stream-chat__msg-name">{displayName}:</span>
                  <span className="stream-chat__msg-content">{msg.content}</span>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {error && <p className="stream-chat__error">{error}</p>}

      <form className="stream-chat__input-row" onSubmit={handleSend}>
        <input
          type="text"
          className="stream-chat__input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={connectionState === 'connected' ? 'Say something…' : 'Connecting…'}
          maxLength={500}
          disabled={sending || connectionState !== 'connected'}
        />
        <button
          type="submit"
          className="stream-chat__send-btn"
          disabled={sending || !input.trim() || connectionState !== 'connected'}
        >
          {sending ? '…' : 'Send'}
        </button>
      </form>
    </div>
  );
}

StreamChat.propTypes = {
  streamerUsername: PropTypes.string.isRequired,
};
